import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert, Layout, Button, Row, Col, DatePicker, message } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import {
	nullSafeGetOrElse,
	nullSafeGet,
	getCurrency,
	isSubContractor,
} from '../../utils/DataAccessUtils';
import BackButton from '../back_button/BackButton';
import { debounce } from '../../utils/PerformanceUtils';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import {
	createInvoiceSubContractor,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
	invoicesRestCrudThunksForSupplier,
	updateInvoiceSubContractor,
} from '../../thunks/invoices_thunks';
import moment from 'moment';
import FormDots from '../common/FormDots';
import { isInternalTech } from '../../utils/AuthUtils';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import {
	getInvoiceDueDateBasedOnPaymentTerms,
	getInvoiceEntityCommonValues,
	initialiseFormDataWithLineItems,
	initializeInvoiceTotalFields,
	initializeLineItemKeys,
	LINE_ITEM_FIELD,
	mergeProposalIntoFormData,
	recalculateInvoiceTotalFields,
} from '../../utils/InvoiceUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import InvoicePaymentTermComponent from '../buyer_invoice_form/InvoicePaymentTermComponent';
import { TIME_IN } from './LaborTimeSelect';
import LaborFormFields from '../buyer_invoice_form/LaborFormFields';
import PartFormFields from '../buyer_invoice_form/PartFormFields';
import EquipmentFormFields from '../buyer_invoice_form/EquipmentFormFields';
import TaxFormFields from '../buyer_invoice_form/TaxFormFields';
import { getContractorWO, getSanitisedWorkOrder } from '../../utils/WorkOrderUtils';
import { getQuoteRestCrudThunksContractorId } from '../quote_utils/QuoteUtils';
import SimpleLineItemFormFields from '../buyer_invoice_form/SimpleLineItemFormFields';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';

interface SupplierInvoiceFormProps extends FormComponentProps {
	formData: any;
	onSuccess: any;
	creating: boolean;
	updating: boolean;
	createErrors: string[];
	updateErrors: string[];
	redirectForwardUrl: string;
	redirectBackwardUrl: string;
	currentUser: any;
	history: any;
	createInvoice: any;
	updateInvoice: any;
	workOrder: any;
	getWorkOrder: any;
	getQuotes: any;
	checkInvoiceNumber: any;
	fetchTaxLineItems: any;
	isClientView: any;
	updateContractorInvoice: any;
	createContractorInvoice: any;
	copyMode: any;
	userType: any;
}

let partUuid = 1;
let laborUuid = 1;
let miscUuid = 1;
let equipmentUuid = 1;
let travelUuid = 1;
let freightUuid = 1;

class SupplierInvoiceForm extends React.Component<SupplierInvoiceFormProps, any> {
	constructor(props) {
		super(props);
		this.state = {
			formDataInitialized: false,
			isUpdate: props.formData.hasOwnProperty('id') && props.formData.id !== undefined,
			laborItemInitialValues: {},
			partItemInitialValues: {},
			equipmentItemInitialValues: {},
			currencyConversionRate: 1,
			location: nullSafeGetOrElse('location', props.formData, {}),
			hasQuote: false,
			editorRef: React.createRef(),
			timeIn: TIME_IN.HOURS,
			formData: {},
		};
	}

	setValuesInState = (val) => this.setState(val);

	recalculateTotals = debounce(() => {
		const { form } = this.props;
		recalculateInvoiceTotalFields(form);
	}, 200);

	initializeFormWithData = (mergedFormData) => {
		const {
			labor,
			part,
			equipment,
			misc,
			travel,
			freight,
			formData: newFormData,
		} = initialiseFormDataWithLineItems(mergedFormData, this.state.timeIn);
		laborUuid = labor;
		partUuid = part;
		equipmentUuid = equipment;
		miscUuid = misc;
		travelUuid = travel;
		freightUuid = freight;

		this.setState({
			formDataInitialized: true,
			formData: { ...mergedFormData, ...newFormData },
		});
	};

	componentDidMount() {
		const { form, getWorkOrder, formData, getQuotes, fetchTaxLineItems, copyMode, isClientView } =
			this.props;
		const { isUpdate } = this.state;

		this.setState({ formDataInitialized: false });

		if (!formData.workOrderId) {
			this.initializeFormWithData(formData);
			return;
		}

		getWorkOrder(formData.workOrderId)
			.then((fetchedWorkOrder) => {
				const contractorWO = getContractorWO(fetchedWorkOrder, copyMode || isClientView);
				let mergedFormData = { ...formData };
				let hasQuote = false;

				if (!isUpdate) {
					const params = contractorWO
						? {
								contractorWorkOrderId: nullSafeGet('id', contractorWO),
								contractorSupplierFacilityId: nullSafeGet(
									'contractorSupplierFacilityId',
									contractorWO
								),
						  }
						: {
								workOrderId: formData.workOrderId,
								supplierFacilityId: fetchedWorkOrder.supplierFacilityId,
						  };

					return getQuotes(params, 'workOrderAssociatedQuotes', !!contractorWO).then(
						(proposals) => {
							if (proposals && proposals.length > 0) {
								hasQuote = true;
								const awardedProposals = proposals.filter(
									(proposal) => proposal.status && proposal.status.toLowerCase() === 'awarded'
								);
								if (awardedProposals.length === 1) {
									mergedFormData = mergeProposalIntoFormData(formData, awardedProposals[0]);
								}
							}
							return { mergedFormData, hasQuote, fetchedWorkOrder };
						}
					);
				}

				return { mergedFormData, hasQuote, fetchedWorkOrder };
			})
			.then(({ mergedFormData, hasQuote, fetchedWorkOrder }) => {
				this.initializeFormWithData(mergedFormData);

				if (hasQuote) {
					this.setState({ hasQuote: true });
				}

				const currency = hasQuote
					? getCurrency({ workOrder: fetchedWorkOrder })
					: getCurrency({ supplier: nullSafeGet('supplierFacility', fetchedWorkOrder) });
				if (currency && currency.id === 'CAD') {
					fetchTaxLineItems(currency.id)
						.then((data) => {
							if (data) {
								form.setFieldsValue({ taxKeys: data });
							}
						})
						.catch((err) => {
							console.error('Error fetching tax line items:', err);
						});
				}
			})
			.catch((err) => {
				console.error('Error loading form data:', err);
				this.initializeFormWithData(formData);
			});
	}

	getScope = () =>
		nullSafeGet('state.editorRef.current', this) &&
		(this.state.editorRef.current as any).getContent();

	handleSubmit = (e) => {
		const {
			form,
			formData,
			redirectForwardUrl,
			currentUser,
			history,
			onSuccess,
			createInvoice,
			updateInvoice,
			workOrder,
			isClientView,
			updateContractorInvoice,
			createContractorInvoice,
			copyMode,
		} = this.props;
		const contractorWO = getContractorWO(workOrder, copyMode || isClientView);
		e.preventDefault();

		form.validateFields((err, values) => {
			if (!err) {
				const submitAction = values.id ? updateInvoice : createInvoice;
				const submitContractorAction = values.id
					? updateContractorInvoice
					: createContractorInvoice;

				const supplierFacilityId = isInternalTech(currentUser)
					? formData.supplierFacilityId
					: values.supplierFacilityId || nullSafeGet('facility.id', currentUser);

				const invoice = {
					...getInvoiceEntityCommonValues(
						values,
						formData,
						this.getScope(),
						currentUser,
						this.state.timeIn
					),
					status: isInternalTech(currentUser) ? 'pending' : 'draft',
					invoicePDFLink:
						values.invoiceTotalAfterTax !== formData.invoiceTotalAfterTax
							? undefined
							: formData.invoicePDFLink,
					supplierFacilityId,
					disputeNotes: formData.disputeNotes,
				};

				if (contractorWO && isSubContractor(currentUser, workOrder)) {
					submitContractorAction({
						...invoice,
						workOrderId: undefined,
						supplierFacilityId: undefined,
						contractorWorkOrderId: contractorWO.id,
						contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
						subSupplierFacilityId: contractorWO.subSupplierFacilityId,
					})
						.then((record) => {
							if (redirectForwardUrl) {
								history.push(redirectForwardUrl);
							}
							if (onSuccess) {
								onSuccess(record);
							}
						})
						.catch((err) => {
							if (err && err.startsWith('Invoice(s) are already associated with this work order')) {
								message.error(err);
								history.goBack();
							}
						});
				} else {
					submitAction(invoice)
						.then((record) => {
							if (redirectForwardUrl) {
								history.push(redirectForwardUrl);
							}
							if (onSuccess) {
								onSuccess(record);
							}
						})
						.catch((err) => {
							if (err && err.startsWith('Invoice(s) are already associated with this work order')) {
								message.error(err);
								history.goBack();
							}
						});
				}
			}
		});
	};

	handleInvoiceNumberChange = async (rule, value, callback) => {
		const { checkInvoiceNumber, formData, workOrder } = this.props;
		const { isUpdate } = this.state;
		if (value) {
			try {
				let data = await checkInvoiceNumber(
					value,
					nullSafeGet('supplierFacilityId', getSanitisedWorkOrder(workOrder))
				);
				if (data && Array.isArray(data)) {
					let hasDuplicate = false;
					data.forEach((invoice) => {
						if (
							(isUpdate && nullSafeGet('id', invoice) !== nullSafeGet('id', formData)) ||
							!isUpdate
						) {
							hasDuplicate = true;
							callback(
								<span>
									Error: Duplicate with{' '}
									<a
										href={'/supplier/invoices/detail/'.concat(
											nullSafeGet('id', invoice),
											`/details`
										)}
										target="_blank"
										rel="noreferrer"
									>
										Invoice {nullSafeGet('id', invoice)}
									</a>
								</span>
							);
						}
					});
					if (!hasDuplicate) {
						callback();
					}
				} else {
					callback();
				}
			} catch (error) {
				// On error, allow the value (don't block form submission)
				callback();
			}
		} else {
			callback();
		}
	};

	onPaymentTermSelect = (val) => {
		const { form } = this.props;
		const { setFieldsValue, getFieldValue } = form;

		const dueDate = getInvoiceDueDateBasedOnPaymentTerms(getFieldValue, val);

		setFieldsValue({
			paymentTerms: val,
			...(dueDate && { dueDate }),
		});
	};

	render() {
		const { creating, updating, updateErrors, createErrors, form, workOrder } = this.props;
		const { formDataInitialized, formData, isUpdate, hasQuote } = this.state;
		if (!formDataInitialized) {
			return <PageLoadingPlaceholder />;
		}
		const submitting = creating || updating;
		const errorMessagesText = createErrors.concat(updateErrors).join('; ');
		let submitText = isUpdate ? 'Update invoice' : 'Create invoice';
		const { getFieldDecorator, getFieldValue } = form;
		const currency =
			hasQuote || isUpdate
				? getCurrency({ workOrder })
				: getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });

		const DATE_FORMAT = 'MM-DD-YYYY';

		getFieldDecorator('id', { initialValue: formData.id });
		getFieldDecorator('attachments', { initialValue: formData.attachments || [] });
		initializeLineItemKeys(formData, getFieldDecorator);
		initializeInvoiceTotalFields(formData, getFieldDecorator);

		//TODO: UPGRADE FORM
		return (
			<Layout.Content>
				<Row className="supplierInvoiceForm buyerInvoiceForm" gutter={16}>
					<Col span={24}>
						<Form hideRequiredMark={true} layout="vertical">
							<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
							{createErrors.length > 0 || updateErrors.length > 0 ? (
								<Form.Item label="">
									<Alert message={errorMessagesText} type="error" />
								</Form.Item>
							) : null}
							{!hasQuote ? (
								<Row>
									<Alert
										style={{
											flex: 1,
											padding: '8px 0px',
											marginLeft: '-8px',
											borderWidth: 0,
											marginBottom: 8,
										}}
										type={'warning'}
										showIcon={false}
										message={
											<span style={{ paddingLeft: '8px' }}>
												Want to submit a invoice in another currency?{' '}
												<a href={'/supplier/company/me'} target={'_blank'} rel="noreferrer">
													Click here
												</a>{' '}
												to change your company currency.
											</span>
										}
									/>
								</Row>
							) : null}
							<div style={{ marginBottom: 32 }}>
								<div className="supplierInvoiceForm__sectionHeadPrimary">Scope</div>
								{getFieldDecorator('scope', {
									initialValue: nullSafeGet(`scope`, formData),
								})(
									<OWRichTextEditor
										initialEditorState={nullSafeGet(`scope`, formData)}
										editorRef={this.state.editorRef}
									/>
								)}
							</div>
							<Form.Item className="supplierInvoiceForm__sectionHeadPrimary" label="Invoice #">
								{getFieldDecorator('invoiceNumber', {
									validateTrigger: ['onBlur'],
									initialValue: nullSafeGet(`invoiceNumber`, formData),
									rules: [
										{
											whitespace: true,
											required: true,
											message: 'This field is required.',
										},
										{
											validator: this.handleInvoiceNumberChange,
										},
									],
								})(<Input style={{ maxWidth: 320 }} />)}
							</Form.Item>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierInvoiceForm__sectionHeadPrimary">Work Order #</div>
								{getFieldDecorator('externalWorkOrderId', {
									initialValue: nullSafeGetOrElse(
										`externalWorkOrderId`,
										formData,
										workOrder && workOrder.id ? `${workOrder.id}` : ''
									),
								})(<Input style={{ maxWidth: 320 }} />)}
							</div>
							<Form.Item className="supplierInvoiceForm__sectionHeadPrimary" label="Invoice Date">
								{getFieldDecorator('dateOfInvoice', {
									initialValue: formData.dateOfInvoice ? moment(formData.dateOfInvoice) : undefined,
									rules: [
										{
											required: true,
											message: 'Please specify invoice date.',
										},
									],
								})(<DatePicker format={DATE_FORMAT} />)}
							</Form.Item>
							<Form.Item className="supplierInvoiceForm__sectionHeadPrimary" label="Payment Terms">
								{getFieldDecorator('paymentTerms', {
									initialValue: nullSafeGet('paymentTerms', formData),
								})(<Input placeholder="Net 30" style={{ maxWidth: 320 }} />)}
								<InvoicePaymentTermComponent onSelect={this.onPaymentTermSelect} />
							</Form.Item>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierInvoiceForm__sectionHeadPrimary">Due Date</div>
								{getFieldDecorator('dueDate', {
									initialValue: formData.dueDate ? moment(formData.dueDate) : undefined,
								})(<DatePicker format={DATE_FORMAT} />)}
							</div>
							<LaborFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialLaborUuid={laborUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<PartFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={partUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<EquipmentFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={equipmentUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.TRAVEL}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={travelUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.FREIGHT}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={freightUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.MISC}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={miscUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierInvoiceForm__sectionHead">
									<div className="supplierInvoiceForm__sectionHeadPrimary">Total Before Tax</div>
									<FormDots />
									<div className="supplierInvoiceForm__sectionHeadSecondary">
										{currency.format(getFieldValue('invoiceTotalBeforeTax'))}
									</div>
								</div>
							</div>
							<TaxFormFields
								formData={formData}
								form={form}
								currency={currency}
								state={this.state}
								recalculateTotals={this.recalculateTotals}
							/>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierInvoiceForm__sectionHead">
									<div className="supplierInvoiceForm__sectionHeadPrimary">Total With Tax</div>
									<FormDots />
									<div className="supplierInvoiceForm__sectionHeadSecondary">
										{currency.format(getFieldValue('invoiceTotalAfterTax'))}
									</div>
								</div>
							</div>
							<Form.Item label="">
								<span className="mr-4">
									<BackButton buttonText="Cancel" />
								</span>
								<Button loading={submitting} type="primary" onClick={(e) => this.handleSubmit(e)}>
									{submitText}
								</Button>
							</Form.Item>
						</Form>
					</Col>
				</Row>
			</Layout.Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	formData: ownProps.formData,
	onSuccess: ownProps.onSuccess,
	updating: state.invoices.updating,
	creating: state.invoices.creating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	redirectBackwardUrl: ownProps.redirectBackwardUrl,
	currentUser: state.session.currentUser,
	history: ownProps.history,
	location: ownProps.location,
	backToWorkOrder: ownProps.backToWorkOrder,
	workOrder: state.work_orders.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchTaxLineItems: (currencyId) => dispatch(fetchTaxLineItemsForSupplier(currencyId)),
	checkInvoiceNumber: (invoice, supplierFacilityId) =>
		dispatch(getInvoiceNumber(invoice, supplierFacilityId)),
	createInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.create(invoice)),
	updateInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.update(invoice)),
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	getQuotes: (params, targetCollectionName, hasContractorId) =>
		dispatch(
			getQuoteRestCrudThunksContractorId(
				ownProps.userType,
				hasContractorId,
				ownProps.isClientView
			).read(params, targetCollectionName)
		),
	createContractorInvoice: (proposal) => dispatch(createInvoiceSubContractor(proposal)),
	updateContractorInvoice: (proposal) => dispatch(updateInvoiceSubContractor(proposal)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierInvoiceForm)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		isClientView: (state as any).clientView.isClientView,
	}),
	() => ({})
)(Form.create<SupplierInvoiceFormProps>()(ComponentWithoutUserType));
